import { useMemo, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import type { Kolom, Startfilter } from '@xxtract/tabelfilters';
import { ariaSort, Filterbalk, SorteerbareKop, useTabelData, useTabelStand } from '@/components/tabel';
import { BEHANDELTIJD_LIJST, bepaalPosities, bouwTabelRegels, type BehandeltijdContext, type TabelRegel } from '../tabel';
import { stapVoorRangschikking, type StapFilter, type StapKeuze } from '../stapFilter';
import { oordeelVoorNorm, type Normen, type Oordeel } from '../normen';
import { OORDEEL_KLEUR } from '../oordeelKleur';
import { naamVoorRegel } from '../naam';
import type { HandlingTimePerPersonRow } from '../handlingTime.api';
import { alsKlokuur, type WerkvensterWeergave } from '../werkvenster';

/**
 * Story 31.4 — de tabel per persoon, uitgesplitst naar de twee stappen.
 *
 * Zes kolommen bij de oplevering: persoon, vastleggen, valideren, totaal, behandelingen en het
 * gemiddelde per stuk. Met de positiekolom van 31.6 en de opsplitsing van 33.1 staan er op "Beide"
 * acht, en op één gekozen stap vijf.
 * Dat laatste zijn er TWEE — één per stap — want één gemiddelde over beide samen beweegt zodra de
 * verhouding verschuift zonder dat iemand sneller of langzamer werkte (epic 31 §3). Sinds story
 * 33.1 zijn dat twee gewone kolommen naast elkaar in plaats van één kolom met twee knoppen in de
 * kop. Hier stond "net als het kerncijfer erboven": dat blok is met story 33.3 vervallen, en het
 * groepsgemiddelde per stuk staat sindsdien alleen nog als lijn in de grafiek en per persoon in
 * deze tabel.
 *
 * Vier dingen die dit onderdeel met opzet NIET doet:
 *
 * - Het maakt van een lege stap geen nul. Een stap zonder meting krijgt een STREEPJE, ook in het
 *   gemiddelde en ook in de behandelingen (epic 31 §1).
 * - Het noemt de kolom niet "stuks". De bron telt stuks ONTDUBBELD; een stuk dat door beide stappen
 *   ging telt hier twee keer, en de som mag dus hoger liggen dan het aantal stuks (epic 31 §4).
 * - Het laat de regel `person-removed` niet meesorteren en niet aanklikken. Die vouwt een onbekend
 *   aantal mensen samen; tussen de mensen in gesorteerd leest hij als één collega, en als filter zou
 *   hij een grafiek van een onbekend aantal mensen als die van één persoon presenteren.
 * - Het velt geen oordeel over wie erboven zit. Sinds story 31.5 kleurt het gemiddelde per stuk
 *   tegen de norm van DÍE stap — groen tot en met de norm, amber tot en met tien procent erboven,
 *   rood daarboven — en verder gebeurt er niets: geen waarschuwing, geen melding, geen gevolg.
 *   Zonder norm blijft de tabel kleurloos, precies zoals vóór die story.
 *
 * Story 31.6 zet er een kolom VÓÓR: de positie.
 *
 * Die rangschikt op het GEMIDDELDE PER STUK van één gekozen stap, en nadrukkelijk niet op minuten.
 * Wie geen positie heeft — omdat hij in die stap niets droeg, of omdat hij helemaal geen collega is
 * — krijgt een streepje en géén nummer; de laatste plaats zou een bewering zijn die er niet is.
 *
 * Story 33.1 haalt de twee knoppenparen weg die hier stonden — "Positie op: vastleggen /
 * valideren" boven de tabel, en "op vastleggen / op valideren" in de kop van de laatste kolom. Ze
 * leken op elkaar, deden iets verschillends en konden elkaar tegenspreken; op productie leverde één
 * klik zichtbare beweging op terwijl de positiekolom op de andere stap bleef slaan. Waar de tabel
 * over gaat komt sindsdien van ÉÉN filter bovenaan het scherm, dat ook de kaarten en de grafiek
 * stuurt. Op één gekozen stap versmalt deze tabel echt: de kolommen van de andere stap zijn er niet,
 * en wie in die stap geen minuut droeg staat er niet — mét zijn naam in de regel eronder, want een
 * collega mag niet geruisloos uit een overzicht verdwijnen waarop gestuurd wordt.
 *
 * En eronder staat de eerlijkheidsnoot, die er ALTIJD staat zolang de positiekolom er staat. Dat is
 * een acceptatiecriterium en geen wens: een ranglijst zonder die regel leest als een oordeel, en
 * daarom zit hij niet achter een knopje, niet in een tekstballon, en niet in kleinere letters dan
 * de rest van de voet van de tabel.
 */

export interface BehandeltijdTabelProps {
  rijen: HandlingTimePerPersonRow[];
  /**
   * De persoon waarop de GRAFIEK gefilterd is, of `null`.
   *
   * Dit filter versmalt de tabel NIET — het beperkt alleen de grafiek (story 31.4). Het stapfilter
   * hieronder doet dat wél; die twee zijn verschillende dingen en mogen niet door elkaar lopen.
   */
  gekozenPersoon: number | null;
  /** `null` betekent "laat het filter los"; de tabel meldt dat ook bij een tweede klik op dezelfde regel. */
  onKiesPersoon: (userId: number | null) => void;
  /**
   * De twee normen (story 31.5).
   *
   * AFWEZIG betekent "dit scherm heeft geen norm om tegen te leggen": de laatste kolom is dan één
   * paar getallen, precies zoals vóór die story — geen merktekens per stap, geen kleur. Aanwezig,
   * óók met twee lege normen, betekent "er wordt tegen een norm gekeken": de twee stappen krijgen
   * dan elk hun eigen merkteken, en dragen een oordeel zodra er voor die stap een norm gezet is.
   */
  normen?: Normen;
  /**
   * De stand van het stapfilter (story 33.1). `beide` is de stand waarin de tabel zich toont zoals
   * vóór die story.
   *
   * Op één stap versmalt de tabel ECHT: de kolommen van de andere stap zijn er niet, en de kolom
   * Totaal ook niet — die zou letterlijk dezelfde getallen dragen als de kolom ernaast (AC-3).
   * Ook de positiekolom volgt deze stand, want hij rangschikt altijd op één stap (AC-10).
   */
  stapFilter?: StapFilter;
  /** Startfilter uit een binnenkomende link (`useStartadres` op de pagina); gaat voor op de bewaarde stand. */
  start?: Startfilter;
  /**
   * Het werkvenster en het gat per persoon (behandeltijd-rapport, CAP-2 en CAP-3).
   *
   * AFWEZIG betekent "geen rol om het te zien": dan staan de twee kolommen er niet, en ook de kop niet.
   */
  werkvenster?: WerkvensterWeergave;
}

/** De lijststand van deze tabel (spec-tabelfilters §7): één tabel, geen routeparameters. */
export const BEHANDELTIJD_TABEL_SLEUTEL = 'behandeltijdTabel';

const SLEUTEL = 'handlingTime.table';

type TabelKolom = Kolom<TabelRegel, BehandeltijdContext>;

/** De positiekolom en de naam: die twee staan er in elke stand, en altijd vooraan. */
const AANTAL_EERSTE_KOLOMMEN = 2;

/**
 * De kop "Gemiddelde" op één gekozen stap: dezelfde kolom, met de kortere kop van vóór story 33.1 —
 * op één stap is er maar één gemiddelde, en de stapnaam staat al in het filter.
 */
const GEMIDDELDE_KOP = 'handlingTime.table.gemiddelde';

/** "Er valt hier niets te oordelen": geen norm, geen meting, of geen persoon om over te oordelen. */
const GEEN_OORDEEL: Oordeel = 'geen';

/** De markering van de gekozen regel, hier vastgelegd zodat hij niet als tekst in de tabel staat. */
const AANGEWEZEN = { 'aria-current': 'true' } as const;

export function BehandeltijdTabel({ rijen, gekozenPersoon, onKiesPersoon, normen, stapFilter = 'beide', start, werkvenster }: BehandeltijdTabelProps) {
  const { t, i18n } = useTranslation();
  const taal = i18n.language;

  /** De stap waarop de positiekolom rangschikt; op "beide" is dat vastleggen, zoals vandaag. */
  const rangStap = stapVoorRangschikking(stapFilter);

  const heelGetal = useMemo(() => new Intl.NumberFormat(taal, { maximumFractionDigits: 0 }), [taal]);
  const eenDecimaal = useMemo(
    () => new Intl.NumberFormat(taal, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    [taal],
  );
  const streepje = t(`${SLEUTEL}.noMinutes`);
  // Een lege uitkomst van het werkvenster en het gat is een streepje van dit scherm, nooit een nul.
  const leeg = t(`${SLEUTEL}.leeg`);

  /** De naam, of de reden waarom er geen naam is. Eén afspraak, in `naam.ts` (story 33.3). */
  function naamVan(regel: TabelRegel): string {
    return naamVoorRegel(regel, t);
  }

  const regels = useMemo(() => bouwTabelRegels(rijen), [rijen]);
  // Over ALLE regels, niet over de gefilterde: een tekstfilter op de naam verandert niemands positie.
  const posities = useMemo(() => bepaalPosities(regels, rangStap), [regels, rangStap]);

  /**
   * De sortering en het filter op de persoon komen uit de centrale bouwstenen (spec-tabelfilters
   * story 6). Standaard: de meeste minuten bovenaan — het totaal, of op één stap die stap. Dat is een
   * volgorde en geen oordeel: het tempo staat in de laatste kolom, niet in de eerste.
   */
  const context = useMemo<BehandeltijdContext>(() => ({ t, stapFilter, posities }), [t, stapFilter, posities]);
  const stand = useTabelStand(BEHANDELTIJD_LIJST, BEHANDELTIJD_TABEL_SLEUTEL, start ? { start } : {});
  // Deze lijst pagineert niet: een `pageSize` uit een link mag geen rijen wegsnijden.
  const data = useTabelData(regels, BEHANDELTIJD_LIJST, { ...stand, paginagrootte: undefined, pagina: 1 }, context);
  const gesorteerd = data.rijen;
  const standaardSortering = BEHANDELTIJD_LIJST.standaardSortering?.(context);
  const zichtbareKolommen = BEHANDELTIJD_LIJST.kolommen.filter((kolom) => kolom.zichtbaar?.(context) ?? true);
  // De sortering zoals hij WERKT: een bewaarde kolom die op deze stap verborgen is, telt niet mee in
  // kop, volgnummer en aankondiging, maar blijft in de stand staan (klikOpKop werkt op `stand`).
  const effectief = stand.sortering.filter((s) => zichtbareKolommen.some((kolom) => kolom.sleutel === s.sleutel));
  const kolommen: TabelKolom[] = zichtbareKolommen
    .map((kolom) => (stapFilter !== 'beide' && kolom.sleutel.startsWith('gemiddelde') ? { ...kolom, kopSleutel: GEMIDDELDE_KOP } : kolom));

  function alsMinuten(waarde: number | null): string {
    return waarde === null ? streepje : heelGetal.format(Math.round(waarde));
  }

  function alsAantal(waarde: number | null): string {
    return waarde === null ? streepje : heelGetal.format(waarde);
  }

  function alsGemiddelde(waarde: number | null): string {
    return waarde === null ? streepje : eenDecimaal.format(waarde);
  }

  /**
   * De twee gemiddelden van één regel, elk tegen de norm van ZÍJN eigen stap.
   *
   * Nooit tegen die van de andere: vastleggen en valideren zijn ongelijksoortig werk, en één norm
   * over beide zou de ene stap beoordelen met de maat van de andere.
   *
   * De regel voor wie uit de gegevens is gehaald blijft KLEURLOOS. Die vouwt een onbekend aantal
   * mensen samen, dus een rood oordeel zou over een groep gaan in plaats van over iemand — dezelfde
   * reden waarom story 31.6 hem geen positie geeft.
   */
  function gemiddeldeVanStap(regel: TabelRegel, stap: StapKeuze): {
    tekst: string;
    oordeel: Oordeel;
    /** Het oordeel als merkteken, of niets: zonder oordeel staat er geen `data-oordeel`. */
    merk: Oordeel | undefined;
    /** Alleen wanneer dit scherm een norm kent; zonder norm blijft de cel zoals hij vóór 31.5 was. */
    testId: string | undefined;
  } {
    const gemiddeld = regel[stap].gemiddeldePerStuk;
    const oordeel: Oordeel = regel.buitenSortering ? GEEN_OORDEEL : oordeelVoorNorm(gemiddeld, normen?.[stap] ?? null);
    return {
      tekst: alsGemiddelde(gemiddeld),
      oordeel,
      merk: oordeel === GEEN_OORDEEL ? undefined : oordeel,
      testId: normen === undefined ? undefined : `handling-time-row-${regel.userId}-gemiddelde-${stap}`,
    };
  }

  function kies(regel: TabelRegel) {
    if (!regel.aanklikbaar) return;
    // Nog een keer op dezelfde regel laat het filter los. Zo is de tabel zelf ook een uitweg, naast
    // de knop bij de grafiek — die blijft de zichtbare manier, dit is de vanzelfsprekende.
    onKiesPersoon(gekozenPersoon === regel.userId ? null : regel.userId);
  }

  /** Waarom iemand geen positie heeft: buiten de rangschikking, geen bevestigde minuten, of niets gemeten in deze stap. */
  function redenGeenPositie(regel: TabelRegel): string {
    if (!regel.aanklikbaar) return t(`${SLEUTEL}.geenPositieBuiten`);
    if (regel.totaalMinuten === null) return t(`${SLEUTEL}.geenPositieGeenMinuten`);
    return t(`${SLEUTEL}.geenPositieNietGemeten`);
  }

  /**
   * De twee kolommen van het werkvenster, na de bestaande kolommen (behandeltijd-rapport, CAP-2 en CAP-3).
   *
   * Een storing is "niet beschikbaar" en een wachtende route is "laden", beide per blok: een storing
   * maakt er nooit een 0 of 0% van. Wie onderaan de tabel staat (de verwijderde persoon en het
   * geautomatiseerde systeem) krijgt geen gat en geen venster, ook niet wanneer de route er toevallig
   * een waarde voor geeft: de verwijderde persoon vouwt een onbekend aantal mensen samen, het systeem is
   * de dienst zelf. Geen van beide is één persoon, dus ook geen storing op hun regel.
   */
  function werkvensterCellen(regel: TabelRegel, weergave: WerkvensterWeergave): ReactNode[] {
    const sleutel = `handling-time-row-${regel.userId}`;
    const geenVenster = regel.onderaanInTabel;
    const status = geenVenster ? null : weergave.status === 'fout'
      ? t(`${SLEUTEL}.nietBeschikbaar`)
      : weergave.status === 'laden' ? t(`${SLEUTEL}.werkvensterLaden`) : null;
    const samenvatting = !geenVenster && weergave.status === 'ok' ? weergave.perPersoon.get(regel.userId) : undefined;
    const gat = samenvatting?.gatGemiddeldMinuten ?? null;
    const procent = samenvatting?.gatPercentage ?? null;
    const begin = samenvatting?.beginMediaan ?? null;
    const eind = samenvatting?.eindMediaan ?? null;

    return [
      <TableCell className="text-right tabular-nums" data-testid={`${sleutel}-gat`} key="gat">
        {status ?? (gat === null || procent === null
          ? leeg
          : t(`${SLEUTEL}.gatWaarde`, { minuten: heelGetal.format(gat), procent: eenDecimaal.format(procent) }))}
      </TableCell>,
      <TableCell className="text-right tabular-nums" data-testid={`${sleutel}-werkvenster`} key="werkvenster">
        {status ?? (
          <>
            <span data-testid={`${sleutel}-werkvenster-begin`}>{begin === null ? leeg : alsKlokuur(begin)}</span>
            {t(`${SLEUTEL}.werkvenstersScheiding`)}
            <span data-testid={`${sleutel}-werkvenster-eind`}>{eind === null ? leeg : alsKlokuur(eind)}</span>
          </>
        )}
      </TableCell>,
    ];
  }

  /**
   * Eén cel, gekozen op de kolom waar hij in staat.
   *
   * De cellen volgen de KOLOMMEN en niet andersom: staat een kolom er in deze stand niet, dan staat
   * de cel er ook niet. Dat is het verschil tussen weglaten en doorstrepen, en het is de reden dat
   * hier één lijst is en niet twee die uit elkaar kunnen lopen (AC-3).
   */
  function cel(regel: TabelRegel, naam: string): ReactNode {
    const sleutel = `handling-time-row-${regel.userId}-${naam}`;

    if (naam === 'vastleggen' || naam === 'valideren' || naam === 'totaal') {
      // Een stap zonder meting draagt een STREEPJE. Nooit een nul: dat is een andere bewering.
      const minuten = naam === 'totaal' ? regel.totaalMinuten : regel[naam].minuten;
      return (
        <TableCell
          className={`text-right tabular-nums ${naam === 'totaal' ? 'font-medium' : ''}`}
          data-testid={sleutel}
          key={naam}
        >
          {alsMinuten(minuten)}
        </TableCell>
      );
    }

    if (naam === 'behandelingen') {
      return (
        <TableCell className="text-right tabular-nums" key={naam}>
          <span className="block" data-testid={sleutel}>{alsAantal(regel.behandelingen)}</span>
          {/* De twee stappen eronder, want de som erboven is nadrukkelijk geen aantal stuks. Op één
              gekozen stap is er niets uit te splitsen: het getal erboven ís die ene stap. */}
          {stapFilter === 'beide' && (
            <span
              className="block text-[11px] text-[var(--foreground-muted)]"
              data-testid={`${sleutel}-uitsplitsing`}
            >
              {t(`${SLEUTEL}.paar`, { een: alsAantal(regel.vastleggen.behandelingen), twee: alsAantal(regel.valideren.behandelingen) })}
            </span>
          )}
        </TableCell>
      );
    }

    if (naam === 'gemiddeldeVastleggen' || naam === 'gemiddeldeValideren') {
      // Elk gemiddelde tegen de norm van ZÍJN eigen stap, nooit tegen die van de andere.
      const stap: StapKeuze = naam === 'gemiddeldeVastleggen' ? 'vastleggen' : 'valideren';
      const waarde = gemiddeldeVanStap(regel, stap);
      return (
        <TableCell className="text-right tabular-nums font-medium" data-testid={sleutel} key={naam}>
          <span className={OORDEEL_KLEUR[waarde.oordeel]} data-oordeel={waarde.merk} data-testid={waarde.testId}>
            {waarde.tekst}
          </span>
        </TableCell>
      );
    }

    return null;
  }

  function opToets(event: KeyboardEvent<HTMLTableRowElement>, regel: TabelRegel) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    kies(regel);
  }

  return (
    <Card className="p-5" data-testid="handling-time-tabel-kaart">
      <div>
        <h2 className="text-sm font-semibold text-[var(--foreground)]">{t(`${SLEUTEL}.titel`)}</h2>
        <p className="mt-1 max-w-[46rem] text-xs text-[var(--foreground-muted)]">{t(`${SLEUTEL}.ondertitel`)}</p>
      </div>

      <div className="mt-3">
        <Filterbalk context={context} data={data} lijst={BEHANDELTIJD_LIJST} stand={{ ...stand, sortering: effectief }} />
      </div>

      <div className="mt-3 overflow-x-auto">
        <Table data-testid="handling-time-table">
          <TableHeader>
            <TableRow>
              {kolommen.map((kolom) => {
                const links = kolom.sleutel === 'persoon' || kolom.sleutel === 'positie';
                return (
                  <TableHead
                    aria-sort={ariaSort(effectief, kolom.sleutel)}
                    className={`px-3 py-2 align-bottom text-xs font-semibold uppercase tracking-wide text-[var(--foreground-muted)] ${links ? 'text-left' : 'text-right'}`}
                    data-testid={`handling-time-sorteer-${kolom.sleutel}`}
                    key={kolom.sleutel}
                    scope="col"
                  >
                    <SorteerbareKop
                      kolom={kolom as Kolom<never, never>}
                      onKlik={() => stand.klikOpKop(kolom.sleutel)}
                      sortering={effectief}
                      standaard={standaardSortering}
                    />
                  </TableHead>
                );
              })}
              {werkvenster !== undefined && (
                <>
                  <TableHead className="px-3 py-2 align-bottom text-xs font-semibold uppercase tracking-wide text-right text-[var(--foreground-muted)]" scope="col">
                    {t(`${SLEUTEL}.gat`)}
                  </TableHead>
                  <TableHead className="px-3 py-2 align-bottom text-xs font-semibold uppercase tracking-wide text-right text-[var(--foreground-muted)]" scope="col">
                    {t(`${SLEUTEL}.werkvenster`)}
                  </TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Het tekstfilter op de persoon liet niemand over: zeg dat, in plaats van een lege tabel. */}
            {gesorteerd.length === 0 && regels.length > 0 && (
              <TableRow>
                <TableCell className="text-sm text-[var(--foreground-muted)]" colSpan={kolommen.length + (werkvenster !== undefined ? 2 : 0)} data-testid="handling-time-geen-treffers">
                  {t('tabel.geenTreffers')}
                </TableCell>
              </TableRow>
            )}
            {gesorteerd.map((regel) => {
              const gekozen = regel.aanklikbaar && gekozenPersoon === regel.userId;
              const positie = posities.get(regel.userId) ?? null;
              return (
                <TableRow
                  {...(gekozen ? AANGEWEZEN : {})}
                  {...(regel.aanklikbaar
                    ? {
                      'aria-label': t(`${SLEUTEL}.${gekozen ? 'kiesPersoonLos' : 'kiesPersoon'}`, { naam: naamVan(regel) }),
                      onClick: () => kies(regel),
                      onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => opToets(event, regel),
                      tabIndex: 0,
                      title: t(`${SLEUTEL}.${gekozen ? 'kiesPersoonLos' : 'kiesPersoon'}`, { naam: naamVan(regel) }),
                    }
                    : {})}
                  className={
                    regel.buitenSortering
                      // Gedempt, en zonder de aanwijzer die belooft dat er iets te klikken valt.
                      ? 'text-[var(--foreground-muted)]'
                      : `cursor-pointer ${gekozen ? 'bg-[var(--primary-faint)]' : ''}`
                  }
                  data-anoniem={regel.buitenSortering ? 'true' : undefined}
                  data-gekozen={gekozen ? 'true' : undefined}
                  data-testid={`handling-time-row-${regel.userId}`}
                  key={regel.userId}
                >
                  {/* Een getal in een kolom en verder niets: geen podium, geen medaille, geen kleur
                      op snel-of-langzaam. Groen, amber en rood blijven voor de norm (AC-11). */}
                  <TableCell className="tabular-nums" data-testid={`handling-time-row-${regel.userId}-positie`}>
                    {positie === null ? (
                      <>
                        <span
                          aria-hidden="true"
                          className="text-[var(--foreground-muted)]"
                          data-testid={`handling-time-row-${regel.userId}-positie-waarde`}
                        >
                          {streepje}
                        </span>
                        <span className="sr-only">
                          {redenGeenPositie(regel)}
                        </span>
                      </>
                    ) : (
                      <span
                        className="font-semibold text-[var(--foreground)]"
                        data-testid={`handling-time-row-${regel.userId}-positie-waarde`}
                      >
                        {heelGetal.format(positie)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    className={`font-medium ${regel.buitenSortering ? 'text-[var(--foreground-muted)]' : 'text-[var(--foreground)]'}`}
                    data-testid={`handling-time-row-${regel.userId}-persoon`}
                  >
                    <span className={gekozen ? 'border-l-2 border-[var(--primary)] pl-2' : ''}>{naamVan(regel)}</span>
                    {regel.buitenSortering && (
                      <span
                        className="mt-0.5 block max-w-[26rem] text-[11px] font-normal leading-snug"
                        data-testid="handling-time-anoniem-uitleg"
                      >
                        {t(`${SLEUTEL}.anoniemUitleg`)}
                      </span>
                    )}
                  </TableCell>
                  {kolommen.slice(AANTAL_EERSTE_KOLOMMEN).map((kolom) => cel(regel, kolom.sleutel))}
                  {werkvenster !== undefined && werkvensterCellen(regel, werkvenster)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Het gat en het werkvenster gaan over de medewerker, ook wanneer er een partij is gekozen (het
          partijfilter werkt alleen op de totalen). Daar hoort een zin bij, anders leest een lezer het
          gat als dat van de gekozen partij. */}
      {werkvenster !== undefined && (
        <p className="mt-3 text-xs leading-relaxed text-[var(--foreground-muted)]" data-testid="handling-time-werkvenster-partij">
          {t(`${SLEUTEL}.werkvenstersPartij`)}
        </p>
      )}

      {/* DE TEGENHANGER VAN DE RANGLIJST, en even zichtbaar als die lijst zelf. Hij staat er altijd:
          niet achter een knopje, niet in een tekstballon, en niet in kleinere letters dan de
          voetnoot hieronder (AC-8 t/m AC-10). */}
      <p
        className="-mx-5 mt-4 flex items-start gap-2 border-t border-[var(--border-soft)] bg-[var(--background-muted)] px-5 py-3 text-xs leading-relaxed text-[var(--foreground)]"
        data-testid="handling-time-eerlijkheidsnoot"
      >
        <span aria-hidden="true" className="mdi mdi-scale-balance mt-px flex-none text-sm text-[var(--foreground-muted)]" />
        <span>{t(`${SLEUTEL}.eerlijkheid`)}</span>
      </p>

      {/* De voetnoot legt uit dat de kolom Behandelingen per stap telt, en dat een stuk over beide
          stappen dus twee keer meetelt. Op één gekozen stap is dat niet waar meer — daar telt een
          stuk één keer — en dan hoort er een andere zin te staan, niet dezelfde met een slag om de
          arm (story 33.1). */}
      <p className="mt-3 text-[11px] leading-snug text-[var(--foreground-muted)]" data-testid="handling-time-tabel-voetnoot">
        {t(`${SLEUTEL}.${stapFilter === 'beide' ? 'voetnoot' : 'voetnootStap'}`)}
      </p>
    </Card>
  );
}
